
import { getSafeDefaultValue, updateSimulatorQueryParams } from '@/utils/simulators';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useForm } from "react-hook-form";
import _isEqualWith from 'lodash/isEqualWith';

const MIN_LOAN_CAPITAL = 0;
const DEFAULT_LOAN_CAPITAL = 100_000;

const MIN_DURATION = 1;
const MAX_DURATION = 100;
const DEFAULT_DURATION = 20;

const MIN_INTEREST_RATE = 0;
const MAX_INTEREST_RATE = 100;
const DEFAULT_INTEREST_RATE = 3.5;

const MIN_INSURANCE_RATE = 0;
const MAX_INSURANCE_RATE = 100;
const DEFAULT_INSURANCE_RATE = 0.15

const DEFAULT_VALUES = {
  loan_capital: DEFAULT_LOAN_CAPITAL,
  duration: DEFAULT_DURATION,
  interest_rate: DEFAULT_INTEREST_RATE,
  insurance_rate: DEFAULT_INSURANCE_RATE,
}

export type MonthlyPaymentsFormValues = {
  loan_capital: number;
    duration: number;
    interest_rate: number;
    insurance_rate: number;
}

export const useMonthlyPaymentsSimulatorForm = () => {
  const initializedRef = React.useRef(false);
  const paramsInitializedRef = React.useRef(false);
    const form = useForm<MonthlyPaymentsFormValues>({
        mode: 'onChange',
        defaultValues: DEFAULT_VALUES,
    });

    const { query, isReady } = useRouter();

    const values = form.watch();

    React.useEffect(() => {
      if (isReady && !initializedRef.current) {
        initializedRef.current = true;
        form.reset({
          loan_capital: getSafeDefaultValue({
            value: query.loan_capital,
            defaultValue: DEFAULT_LOAN_CAPITAL,
            min: MIN_LOAN_CAPITAL,
          }),
          duration: getSafeDefaultValue({
            value: query.duration,
            defaultValue: DEFAULT_DURATION,
            min: MIN_DURATION,
            max: MAX_DURATION,
          }),
          interest_rate: getSafeDefaultValue({
            value: query.interest_rate,
            defaultValue: DEFAULT_INTEREST_RATE,
            min: MIN_INTEREST_RATE,
            max: MAX_INTEREST_RATE,
          }),
          insurance_rate: getSafeDefaultValue({
            value: query.insurance_rate,
            defaultValue: DEFAULT_INSURANCE_RATE,
            min: MIN_INSURANCE_RATE,
            max: MAX_INSURANCE_RATE,
          }),
        })
      } else if (!_isEqualWith(DEFAULT_VALUES, values) || paramsInitializedRef.current) {
        paramsInitializedRef.current = true;
        updateSimulatorQueryParams(values);
      }
    }, [isReady, query, form, values])

    const loanCapital = form.watch('loan_capital');
    const interestRate = form.watch('interest_rate');
    const insuranceRate = form.watch('insurance_rate');
    const duration = form.watch('duration');
    const numbersOfYears = (Number.isNaN(duration) || duration < 0) ? 0 : duration;

    const isInvalid = Number.isNaN(loanCapital) || 
      Number.isNaN(interestRate) || 
      numbersOfYears < MIN_DURATION ||
      numbersOfYears > MAX_DURATION ||
      interestRate < MIN_INTEREST_RATE ||
      interestRate > MAX_INTEREST_RATE ||
      insuranceRate < MIN_INSURANCE_RATE ||
      insuranceRate > MAX_INSURANCE_RATE ||
      loanCapital < MIN_LOAN_CAPITAL;

    return {
        form,
        isInvalid,
        loanCapital,
        interestRate: interestRate / 100,
        insuranceRate: insuranceRate / 100,
        numbersOfYears,
    }
}